// P&L formatters. The one rule: null / undefined / NaN is UNKNOWN and renders
// as '—', never as $0.00. (The shared formatters elsewhere in the app do
// Number(v || 0), which would silently turn a missing Xero month into a zero.)

export function isNum(n) {
  return n !== null && n !== undefined && n !== '' && Number.isFinite(Number(n))
}

/** $1,234 / −$1,234 (dp decimals). Unknown → '—'. */
export function money(n, dp = 0) {
  if (!isNum(n)) return '—'
  const v = Number(n)
  const s = Math.abs(v).toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp })
  // Avoid a "−$0" for values that round to zero.
  const rounded = Math.round(Math.abs(v) * 10 ** dp) / 10 ** dp
  return (v < 0 && rounded !== 0 ? '−$' : '$') + s
}

/** A fraction (0.351) as '35.1%'. Unknown → '—'. */
export function pct(n, dp = 1) {
  if (!isNum(n)) return '—'
  return `${(Number(n) * 100).toFixed(dp)}%`
}

/** Plain count with thousands separators. Unknown → '—'. */
export function num(n, dp = 0) {
  if (!isNum(n)) return '—'
  return Number(n).toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp })
}

/** Divide, returning null when either side is unknown or the divisor is 0. */
export function safeDiv(a, b) {
  if (!isNum(a) || !isNum(b) || Number(b) === 0) return null
  return Number(a) / Number(b)
}

/** Tailwind class for a signed value: red when negative. */
export function negClass(n) {
  return isNum(n) && Number(n) < 0 ? 'text-red-600' : ''
}

/** Best human message out of an axios error. */
export function errMsg(err) {
  const d = err?.response?.data?.detail
  if (typeof d === 'string') return d
  if (Array.isArray(d)) return d.map((x) => x?.msg || String(x)).join('; ')
  if (d && typeof d === 'object') return d.message || JSON.stringify(d)
  return err?.message || 'Something went wrong'
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Weekday index (0 = Sun) of a 'YYYY-MM-DD', computed in UTC so it never shifts. */
export function dowOf(iso) {
  const [y, m, d] = String(iso).split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay()
}

/** 'Tue 27 Oct'. */
export function dayLabel(iso) {
  if (!iso) return '—'
  const [, m, d] = String(iso).split('-').map(Number)
  return `${DOW[dowOf(iso)]} ${d} ${MON[m - 1]}`
}

/**
 * Weekday name from the API. Accepts a name ('Monday'/'Mon') or a number in
 * Python's date.weekday() convention (0 = Monday … 6 = Sunday), which is what
 * the backend uses.
 */
export function weekdayName(w) {
  if (typeof w === 'string' && w && !/^\d+$/.test(w)) return w.slice(0, 3)
  const n = Number(w)
  if (!Number.isInteger(n) || n < 0 || n > 6) return String(w ?? '—')
  return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][n]
}

/** 'Mon 2 Nov, 8:15am' — AU time for UTC audit timestamps. */
export function whenAu(ts) {
  if (!ts) return null
  try {
    return new Date(ts).toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney', weekday: 'short', day: 'numeric', month: 'short',
      hour: 'numeric', minute: '2-digit',
    })
  } catch {
    return String(ts)
  }
}
